import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

// Coordinates the two-layer branded splash: the native Capacitor SplashScreen (covers the
// gap between process start and the WebView painting) and the HTML/CSS overlay defined in
// index.html (#app-splash — covers the gap between the WebView painting and the first
// screen actually having data, since that's several seconds on a cold Angular bootstrap).
// hide() is idempotent and safe to call from multiple race-prone call sites (dashboard data
// load, non-dashboard first navigation, and the hard timeout below) — only the first call
// does anything.
//
// Deliberately built to NOT repeat the offline-freeze bug fixed in v2.15.3 (a full-screen
// PrimeNG overlay mask that only got removed on a CSS animationend event, which a fast
// offline navigation could cancel before it ever fired — leaving an invisible, permanent,
// click-eating div). This overlay is removed two different ways, NEITHER of which depends
// on any animation/transition event actually completing:
// 1. pointerEvents is set to 'none' synchronously, in the same tick hide() is called —
//    before any fade/removal even starts — so clicks pass through immediately regardless
//    of what the CSS transition does.
// 2. The element itself is deleted from the DOM via a plain setTimeout (a JS timer, which
//    always fires on schedule irrespective of animation/paint state), not an animationend
//    listener (which can silently never fire if the animation gets cancelled/skipped).
const DEFAULT_SAFETY_TIMEOUT_MS = 8000;
// A different-user switch on this device does real work before the app can safely continue
// (see announceAccountSwitch below) — a normal cold boot never needs this long, but this path
// legitimately can, and "never strand the user" still wins: bounded and generous, not infinite.
const ACCOUNT_SWITCH_SAFETY_TIMEOUT_MS = 12000;
const MESSAGE_CYCLE_MS = 2200;
const TEXT_CROSSFADE_MS = 380;

// The native Capacitor splash only has one job: cover the gap between process launch and the
// WebView painting its first frame. Once the WebView has painted, index.html's own #app-splash
// overlay takes over. Dismissing the native splash LATE (on the 8s safety net, or on a heavy
// route's first NavigationEnd) was landing the dismissal mid-render on some MediaTek/Vivo GPUs
// and killing the WebView's native surface — a frozen screen recoverable only by a screen
// lock/unlock (logcat: `E/GPUAUX: Null anb` flood). So the native splash is now dismissed on a
// short fixed timer, instantly (no fade animation to race), during the calm window right after
// bootstrap — decoupled entirely from the HTML overlay's own data-ready lifecycle below.
const NATIVE_SPLASH_HIDE_MS = 1200;

@Injectable({ providedIn: 'root' })
export class AppSplashService {
  private hidden = false;
  private nativeSplashHidden = false;
  private safetyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private messageIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.armSafetyTimeout(DEFAULT_SAFETY_TIMEOUT_MS);
    if (Capacitor.isNativePlatform()) {
      setTimeout(() => this.hideNativeSplashNow(), NATIVE_SPLASH_HIDE_MS);
    }
  }

  // Instantly dismiss ONLY the native Capacitor splash (no fade — fadeOutDuration 0 — so there
  // is no animation to race the WebView compositor). Idempotent. The HTML #app-splash overlay
  // is still up at this point and keeps covering the screen until real data has loaded, so
  // there is no visual flash.
  hideNativeSplashNow(): void {
    if (this.nativeSplashHidden || !Capacitor.isNativePlatform()) return;
    this.nativeSplashHidden = true;
    SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => { /* already hidden — not fatal */ });
  }

  // Safety net: nothing should ever be able to strand a user behind the splash — if some
  // future landing route/component never calls hide(), force it after a generous timeout.
  // Re-armable so announceAccountSwitch() below can extend the budget for its own longer path
  // without weakening the normal cold-boot timeout for every other login.
  private armSafetyTimeout(ms: number): void {
    if (this.safetyTimeoutId) clearTimeout(this.safetyTimeoutId);
    this.safetyTimeoutId = setTimeout(() => this.hide(), ms);
  }

  // Called by AuthSessionGuard the instant it detects a different user logging in on this device
  // — right before the (now properly awaited, see that guard's own comment) IndexedDB wipe
  // starts. Login success does a full page reload (window.location.href, not an Angular route
  // navigation — see login.component.ts), so this splash element is always freshly on screen
  // at this exact point in the flow, same as a normal cold boot; this just swaps its static
  // "LumberLinq" text for a couple of gently crossfading, non-technical reassurances for as
  // long as the wipe + this guard's own navigation takes, with a richer glow and a longer
  // safety-net budget to match. Deliberately no mention of accounts/switching/clearing/cache —
  // this should read as "something good is happening," never as a warning or a technical aside.
  // No-op if the splash is already gone.
  announceAccountSwitch(): void {
    if (this.hidden) return;
    const textEl = document.querySelector('#app-splash .app-splash-text') as HTMLElement | null;
    const wrapEl = document.querySelector('#app-splash .app-splash-logo-wrap') as HTMLElement | null;
    if (!textEl) return;

    wrapEl?.classList.add('app-splash-emphasis');
    this.armSafetyTimeout(ACCOUNT_SWITCH_SAFETY_TIMEOUT_MS);

    const messages = ['Getting your workspace ready…', 'Almost there…'];
    let i = 0;
    const swap = () => {
      textEl.classList.add('app-splash-text-out');
      setTimeout(() => {
        if (this.hidden) return;
        i = (i + 1) % messages.length;
        textEl.textContent = messages[i];
        textEl.classList.remove('app-splash-text-out');
      }, TEXT_CROSSFADE_MS);
    };
    textEl.textContent = messages[0];
    this.messageIntervalId = setInterval(swap, MESSAGE_CYCLE_MS);
  }

  hide(): void {
    if (this.hidden) return;
    this.hidden = true;

    if (this.safetyTimeoutId) {
      clearTimeout(this.safetyTimeoutId);
      this.safetyTimeoutId = null;
    }
    if (this.messageIntervalId) {
      clearInterval(this.messageIntervalId);
      this.messageIntervalId = null;
    }

    const overlay = document.getElementById('app-splash');
    if (overlay) {
      (overlay as HTMLElement).style.pointerEvents = 'none';
      overlay.classList.add('app-splash-hide');
      setTimeout(() => overlay.remove(), 400);
    }

    // Backup only — the native splash is normally already gone via the constructor's
    // NATIVE_SPLASH_HIDE_MS timer well before this runs. Still idempotent + instant.
    this.hideNativeSplashNow();
  }
}
